'use client';

import { AnimatePresence, motion } from 'framer-motion';
import { PortableText } from 'next-sanity';
import { PortableTextBlock } from 'sanity';
import { cn } from '@/lib/utils';
import { GROQImage } from '@/sanity/lib/definitions';
import { GroqImage } from '@/helpers/Image';
import { richTextComponent } from '@/sanity/sanityPortableText';

export interface FeatureCardData {
  _key?: string;
  icon?: string;
  eyebrow?: string;
  content?: PortableTextBlock[];
}

export function FeatureCardIcon({
  icon,
  className,
}: {
  icon?: string;
  className?: string;
}) {
  const base = {
    className: cn('w-[3rem] h-[3rem] shrink-0 text-current', className),
    viewBox: '0 0 24 24',
    fill: 'none' as const,
    stroke: 'currentColor',
    strokeWidth: 1.75,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
    'aria-hidden': true as const,
  };

  switch (icon) {
    case 'shield-star':
      return (
        <svg {...base}>
          <path d="M12 3l7 3v5c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V6l7-3Z" />
          <path d="m12 8 .9 1.8 2 .3-1.45 1.4.35 2-1.8-.95-1.8.95.35-2-1.45-1.4 2-.3L12 8Z" />
        </svg>
      );
    case 'chart-dots':
      return (
        <svg {...base}>
          <path d="M4 19V9" />
          <path d="M10 19V5" />
          <path d="M16 19v-8" />
          <path d="M22 19v-4" />
          <circle cx="4" cy="7" r="1" fill="currentColor" stroke="none" />
          <circle cx="10" cy="3" r="1" fill="currentColor" stroke="none" />
          <circle cx="16" cy="9" r="1" fill="currentColor" stroke="none" />
          <circle cx="22" cy="13" r="1" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'adjustments':
      return (
        <svg {...base}>
          <path d="M4 6h16" />
          <path d="M4 12h16" />
          <path d="M4 18h16" />
          <circle cx="8" cy="6" r="2" />
          <circle cx="16" cy="12" r="2" />
          <circle cx="10" cy="18" r="2" />
        </svg>
      );
    case 'bolt':
      return (
        <svg {...base}>
          <path d="M13 3L4 14h7l-1 7 9-11h-7l1-7z" />
        </svg>
      );
    case 'shield-heart':
      return (
        <svg {...base}>
          <path d="M12 3l7 3v5c0 5-3.5 8.5-7 10-3.5-1.5-7-5-7-10V6l7-3Z" />
          <path d="M9.5 11.5a2.5 2.5 0 0 1 5 0c0 .83-.37 1.57-.96 2.07L12 15l-1.54-1.43A2.5 2.5 0 0 1 9.5 11.5Z" />
        </svg>
      );
    case 'scale':
      return (
        <svg {...base}>
          <path d="M12 4v14" />
          <path d="M8 7h8" />
          <path d="M5 7 3 11h4L5 7Z" />
          <path d="m19 7-2 4h4l-2-4Z" />
          <path d="M7 20h10" />
        </svg>
      );
    case 'file-analytics':
      return (
        <svg {...base}>
          <path d="M14 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8Z" />
          <path d="M14 3v5h5" />
          <path d="M9 17v-4" />
          <path d="M12 17v-7" />
          <path d="M15 17v-2" />
        </svg>
      );
    case 'key':
      return (
        <svg {...base}>
          <circle cx="8" cy="15" r="4" />
          <path d="M12 15h8" />
          <path d="M18 15v-2" />
          <path d="M15 15v2" />
        </svg>
      );
    case 'door-enter':
      return (
        <svg {...base}>
          <path d="M5 21V5a2 2 0 0 1 2-2h8" />
          <path d="M19 21V3" />
          <path d="M10 12h11" />
          <path d="m18 9 3 3-3 3" />
        </svg>
      );
    case 'wallet':
      return (
        <svg {...base}>
          <path d="M4 7a2 2 0 0 1 2-2h11a2 2 0 0 1 2 2v1H6a2 2 0 0 0-2 2V7Z" />
          <path d="M4 10a2 2 0 0 1 2-2h13v9a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-7Z" />
          <circle cx="16" cy="13.5" r="1" fill="currentColor" stroke="none" />
        </svg>
      );
    case 'graph':
      return (
        <svg {...base}>
          <path d="M4 19h16" />
          <path d="M6 16 11 11l3 3 5-6" />
          <path d="M16 8h3v3" />
        </svg>
      );
    default:
      return (
        <svg {...base}>
          <rect x="5" y="5" width="14" height="14" rx="2" />
          <path d="M8 12h8" />
        </svg>
      );
  }
}

export function FeatureCard({
  card,
  active,
  onActivate,
  hoverEnabled,
  dotsBg,
  variant = 'home',
  className,
}: {
  card: FeatureCardData;
  active: boolean;
  onActivate: () => void;
  hoverEnabled: boolean;
  dotsBg?: GROQImage;
  variant?: 'home' | 'about-us';
  className?: string;
}) {
  const iconBox = (
    <div
      className={cn(
        'relative grid shrink-0 place-items-center rounded-[0.25rem] border transition-colors duration-300',
        variant === 'about-us'
          ? 'h-[7.25rem] w-[7.25rem]'
          : 'h-[7.25rem] w-[7.25rem]',
        active
          ? 'border-white/10 bg-white/5 text-white'
          : 'border-[rgba(0,27,20,0.1)] bg-transparent text-[#001b14]',
      )}
    >
      <div
        className={cn(
          'absolute inset-[0.75rem] rounded-[0.25rem] transition-colors duration-300',
          active ? 'bg-white/10 backdrop-blur-sm' : 'bg-[rgba(0,27,20,0.05)]',
        )}
      />
      <FeatureCardIcon icon={card.icon} className="relative z-10" />
    </div>
  );

  return (
    <article
      onMouseEnter={hoverEnabled ? onActivate : undefined}
      onClick={onActivate}
      className={cn(
        'group relative flex w-full overflow-hidden border p-[4rem] transition-colors duration-300',
        variant === 'home'
          ? 'min-h-[23.72225rem] flex-col justify-between'
          : 'flex-col gap-[3rem]',
        active
          ? 'border-[rgba(47,47,47,0.1)] bg-[#0d2922] text-white'
          : 'border-[rgba(47,47,47,0.1)] bg-white text-[#001b14]',
        className,
      )}
    >
      <AnimatePresence initial={false}>
        {active && (
          <motion.div
            key="active-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.22, ease: [0.25, 0.1, 0.25, 1] }}
            className="absolute inset-0 overflow-hidden"
          >
            <div className="absolute inset-0 z-0 bg-[linear-gradient(90deg,rgba(22,56,46,0.72)_0%,rgba(16,41,34,0.46)_38%,rgba(13,41,34,0)_100%)]" />
            {dotsBg ? (
              <motion.div
                initial={{ opacity: 0, scale: 1.015 }}
                animate={{ opacity: 0.7, scale: 1 }}
                exit={{ opacity: 0, scale: 1.01 }}
                transition={{ duration: 0.28, ease: [0.25, 0.1, 0.25, 1] }}
                className="absolute inset-0 z-[1] mix-blend-luminosity"
              >
                <GroqImage
                  src={dotsBg.src}
                  alt=""
                  width={dotsBg.width}
                  height={dotsBg.height}
                  className="!h-full !w-full object-cover"
                />
              </motion.div>
            ) : (
              <motion.div
                initial={{ opacity: 0 }}
                animate={{ opacity: 0.7 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.22, ease: [0.25, 0.1, 0.25, 1] }}
                className="absolute inset-0 z-[1] mix-blend-luminosity bg-[radial-gradient(circle,rgba(255,255,255,0.18)_0%,rgba(255,255,255,0.18)_0.11rem,transparent_0.13rem)] [background-size:0.95rem_0.95rem] [background-position:0_0]"
              />
            )}
          </motion.div>
        )}
      </AnimatePresence>

      {variant === 'home' ? (
        <>
          {card.eyebrow && (
            <p
              className={cn(
                'relative z-10 mb-0 font-geist-mono text-[1.75rem] font-semibold uppercase leading-[1.33]',
                active ? 'text-[#8fbeab]' : 'text-[#6da991]',
              )}
            >
              {card.eyebrow}
            </p>
          )}
          <div className="relative z-10 flex items-center gap-[3rem]">
            {iconBox}
            {card.content && (
              <div
                className={cn(
                  'richtext richtext-start flex-1 flex flex-col gap-[rem] prose-headings:text-[2.75rem] prose-p:text-[2.75rem]',
                  active
                    ? 'prose-p:text-white prose-headings:text-white prose-strong:text-white'
                    : 'prose-p:text-[#001b14] prose-headings:text-[#001b14] prose-strong:text-[#001b14]',
                )}
              >
                <PortableText
                  value={card.content}
                  components={richTextComponent}
                />
              </div>
            )}
          </div>
        </>
      ) : (
        <>
          <div className="relative z-10">{iconBox}</div>
          {card.content && (
            <div
              className={cn(
                'richtext richtext-start relative z-10 flex flex-col gap-[1.5rem] prose-headings:text-[2.25rem] prose-headings:font-medium prose-p:text-[2rem] prose-headings:tracking-[-0.125rem]',
                active
                  ? 'prose-p:text-white prose-headings:text-white prose-strong:text-white'
                  : 'prose-p:text-[#001b14] prose-headings:text-[#001b14] prose-strong:text-[#001b14]',
              )}
            >
              <PortableText
                value={card.content}
                components={richTextComponent}
              />
            </div>
          )}
        </>
      )}
    </article>
  );
}
